import React, { useCallback, useEffect, useState, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { debounce } from "lodash";
import { isMobile } from "react-device-detect";
import { useProjectsData } from "./projectsDATA";
import { logAnalytics } from "../../services/firebase/analytics";
import { useTranslation } from "react-i18next";

const _ProjectCard: React.FC<{
  index: number;
  card: {
    title: string;
    subtitle: string;
    image: string;
    details: string;
  };
  onCardViewed: (index: number) => void;
}> = ({ card, onCardViewed, index }) => {
  const { t } = useTranslation();
  const [isViewed, setIsViewed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isViewed) {
      onCardViewed(index);
    }
  }, [isViewed, index, onCardViewed]);

  const handleMouseEnter = useCallback(() => {
    if (!isMobile && !isViewed) {
      setIsViewed(true);
    }
  }, [isViewed]);

  const handleClick = useCallback(() => {
    if (isMobile && !isViewed) {
      setIsViewed(true);
    }
  }, [isViewed]);

  const toggleExpanded = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click events
    setIsExpanded(!isExpanded);
    
    // Log analytics for expand/collapse
    logAnalytics(isExpanded ? "project_card_collapsed" : "project_card_expanded", {
      project_title: card.title,
    });
  }, [isExpanded, card.title]);

  // Check if text needs truncation (rough estimation)
  const needsTruncation = card.details.length > 200; // Adjust this threshold as needed

  // Prevent mouse events on mobile to avoid conflicts
  const cardProps = isMobile ? {
    onClick: handleClick
  } : {
    onMouseEnter: handleMouseEnter
  };

  return (
    <div className="col">
      <div 
        {...cardProps}
        ref={cardRef}
        className={`project-card ${isExpanded ? 'expanded' : ''}`}
      >
        <div className="project-card__image">
          <img 
            src={card.image} 
            alt={card.title} 
            className="card-img-top"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="project-card__body">
          <h4 className="card-title">
            <strong>{card.title}</strong>
          </h4>
          <p className="card-subtitle">
            <span className="highlighted-span">{card.subtitle}</span>
          </p>
          <p
            className="card-text mt-3"
            style={!isExpanded ? {
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            } : {}}
          >
            {card.details}
          </p>
          {needsTruncation && (
            <button 
              className="read-more-btn"
              onClick={toggleExpanded}
              type="button"
            >
              {isExpanded 
                ? (t('common:buttons.readLess') || 'Leggi meno') 
                : (t('common:buttons.readMore') || 'Leggi tutto')
              }
              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProjectsCards = () => {
  const { t } = useTranslation();
  const projectsData = useProjectsData();
  const [data, setData] = useState(projectsData.slice(0, 3));
  const [viewedProjects, setViewedProjects] = useState<number[]>([]);

  // Debug: Log the data we're receiving
  console.log('ProjectsCards - projectsData:', projectsData);
  console.log('ProjectsCards - data state:', data);

  // Update data when projects data changes (language switch)
  useEffect(() => {
    setData(projectsData.slice(0, 3));
  }, [projectsData]);

  const onCardViewed = (index: number) => {
    console.log("onCardViewed", index, viewedProjects);
    if (!viewedProjects.includes(index)) {
      // log the event on firebase
      logAnalytics("project_card_viewed", {
        project_title: data[index].title,
      });
      console.log("project_card_viewed", {
        project_title: data[index].title,
      });

      // add the index to the viewed projects
      setViewedProjects([...viewedProjects, index]);
    }
  };

  return (
    <>
      <div className="FlipCardComponent">
        {data.map((card, index) => (
          <_ProjectCard
            key={index}
            card={card}
            index={index}
            onCardViewed={onCardViewed}
          />
        ))}
      </div>

      <div
        className="section__cta mt-5 border-none "
        onClick={() => {
          if (data.length < projectsData.length) {
            setData([
              ...data,
              ...projectsData.slice(data.length, data.length + 3),
            ]);

            logAnalytics("show_more_projects");
          } else {
            setData(projectsData.slice(0, 3));
          }
        }}
      >
        <span>
          {data.length < projectsData.length
            ? t('common:buttons.showMore') || 'Mostra di più'
            : t('common:buttons.showLess') || 'Mostra di meno'}
        </span>
        {data.length < projectsData.length && (
          <i
            className="fas fa-chevron-right"
            style={{
              fontSize: "1.5rem",
              marginLeft: "1rem",
            }}
          ></i>
        )}
      </div>
    </>
  );
};

export default ProjectsCards;
